/**
 * @file
 * @fileOverview This file contains the HTMLToCanvasTexture class for converting HTML markup to a canvas texture.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

import domtoimage from 'dom-to-image';

/**
 * HTMLToCanvasTexture class
 * @class
 * @classdesc A class that converts HTML markup to a canvas texture for use in 3D environments.
 */
class HTMLToCanvasTexture {
    /**
   * Creates a new HTMLToCanvasTexture instance.
   * @constructor
   * @param {string} htmlMarkup - The HTML markup to be converted to a texture.
   * @param {string} canvasId - The ID of the canvas element to draw on.
   * @param {string} planeId - The ID of the 3D plane element to apply the texture to.
   * @param {number} [canvasWidth=0] - The width of the canvas. If 0, uses the default size.
   * @param {number} [canvasHeight=0] - The height of the canvas. If 0, uses the default size.
   * @param {number} [scale=1] - The scale factor for rendering.
   */
  constructor(
    htmlMarkup,
    canvasId,
    planeId,
    canvasWidth = 0,
    canvasHeight = 0,
    scale = 1
  ) {
    this.htmlMarkup = htmlMarkup;
    this.canvasId = canvasId;
    this.planeId = planeId;
    this.scale = scale;
    this.templateId = this.generateUniqueTemplateId();
    this.createTemplate();
    this.textureLoader = new THREE.TextureLoader();
  }

  /**
   * Generates a unique template ID.
   * @private
   * @returns {string} A unique template ID.
   */
  generateUniqueTemplateId() {
    return 'template-' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Creates a template element with the HTML markup and adds it to the document body.
   * @private
   */
  createTemplate() {
    const template = document.createElement('div');
    template.id = this.templateId;
    template.innerHTML = this.htmlMarkup;
    document.body.appendChild(template);
  }

  /**
   * Applies CSS styles to the template element.
   * @param {Object} styles - An object containing CSS styles to apply.
   */
  applyCSSStyles(styles) {
    const template = document.getElementById(this.templateId);
    for (const [key, value] of Object.entries(styles)) {
      template.style[key] = value;
    }
  }

 /**
   * Draws the HTML content to the canvas and updates the 3D plane texture.
   */
  drawHTMLToCanvas() {
    const content = document.getElementById(this.templateId);
    let _this = this;
    domtoimage.toPng(content).then(function (dataUrl) {
      _this.applyTexture(document.getElementById(_this.planeId), dataUrl);
    });
  }

  applyTexture(plane, dataUrl) {
    if (!(plane instanceof HTMLElement) || !plane.matches('a-plane')) {
      throw new Error('First argument must be an a-plane element');
    }

    /*if (!(blob instanceof Blob)) {
      throw new Error('Second argument must be a Blob');
    }*/

    const mesh = plane.getObject3D('mesh');
    if (!mesh) {
      throw new Error('Plane does not have a mesh object');
    }

    this.textureLoader.load(
      dataUrl,
      (texture) => {
        texture.encoding = THREE.sRGBEncoding;
        texture.flipY = true;
        this.disposeOldTexture(mesh);
        this.applyNewTexture(mesh, texture);
      },
      undefined,
      (error) => {
        console.error('Error loading texture:', error);
      }
    );
  }

  disposeOldTexture(mesh) {
    if (mesh.material.map) {
      mesh.material.map.dispose();
      if (mesh.material.map.source && mesh.material.map.source.data) {
        URL.revokeObjectURL(mesh.material.map.source.data);
      }
    }
  }

  applyNewTexture(mesh, texture) {
    mesh.material.map = texture;
    mesh.material.needsUpdate = true;
  }

  updateHTML(newHtmlMarkup) {
    this.htmlMarkup = newHtmlMarkup;
    const template = document.getElementById(this.templateId);
    template.innerHTML = this.htmlMarkup;
    this.drawHTMLToCanvas();
  }

  getHTML() {
    return this.htmlMarkup;
  }

  getTemplateId() {
    return this.templateId;
  }
}

export default HTMLToCanvasTexture;
